/**
 * Menu Import Modal - Handles the complete import flow
 */

import React, { useState, useRef, useCallback } from 'react';
import Modal from '../comman/modal.js';
import Button from '../comman/button.js';
import FileUpload from '../comman/FileUpload.js';
import ImportPreview from '../Menu/ImportPreview.js';
import ProgressBar from '../comman/ProgressBar.js';
import LoadingSpinner from '../LoadingSpinner/loadingSpinner.js';
import Toast from '../Toast/toast.js';
import { useLanguage } from '../../utils/languageContext.js';
import { getTranslation } from '../../utils/translate.js';
import { parseCSVFile, parseExcelFile, mapRowToDishData, createFilteredFile } from '../../utils/importUtils.js';
import { batchValidateRows, createValidationContext } from '../../utils/importValidation.js';
import { importDishesFromFile, downloadImportTemplate, getAllMenus, fetchDishTags, fetchDishCuisineTags } from '../../services/menu-service.js';
import { useAuth } from '../../utils/authContext.js';

/**
 * MenuImportModal component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onImportComplete - Callback when import completes
 */
const MenuImportModal = ({ isOpen, onClose, onImportComplete }) => {
  const { language } = useLanguage();
  const { chefData } = useAuth();
  const toastRef = useRef(null);

  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1: File selection, 2: Preview, 3: Importing, 4: Results

  // File state
  const [dataFile, setDataFile] = useState(null);
  const [imageZipFile, setImageZipFile] = useState(null);
  const [parsing, setParsing] = useState(false);

  // Data state
  const [parsedData, setParsedData] = useState([]);
  const [mappedDishes, setMappedDishes] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Context data
  const [existingDishes, setExistingDishes] = useState([]);
  const [tags, setTags] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [loadingContext, setLoadingContext] = useState(false);

  // Import state
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const abortControllerRef = useRef(null);

  // Load context data (existing dishes, tags, cuisines)
  const loadContextData = useCallback(async () => {
    if (!chefData?.id) return;

    setLoadingContext(true);
    try {
      const [dishesResponse, tagsData, cuisinesData] = await Promise.all([
        getAllMenus(chefData.id, 0, 1000), // Get all dishes for duplicate check
        fetchDishTags(),
        fetchDishCuisineTags()
      ]);

      setExistingDishes(dishesResponse?.dishDtoTreeSet || []);
      setTags(tagsData || []);
      setCuisines(cuisinesData || []);
    } catch (error) {
      console.error('Error loading context data:', error);
      toastRef.current?.addToast(
        getTranslation('import.importErrorMessages.loadContextFailed', language) || 'Failed to load validation data',
        'error'
      );
    } finally {
      setLoadingContext(false);
    }
  }, [chefData?.id, language]);

  // Load context when modal opens
  React.useEffect(() => {
    if (isOpen) {
      loadContextData();
    }
  }, [isOpen, loadContextData]);

  // Handle data file selection
  const handleDataFileSelect = useCallback(async (file, error) => {
    if (error) {
      toastRef.current?.addToast(error, 'error');
      return;
    }

    if (!file) {
      setDataFile(null);
      setParsedData([]);
      setMappedDishes([]);
      setValidationResults([]);
      return;
    }

    setDataFile(file);
    setParsing(true);

    try {
      // Parse file based on extension
      const extension = file.name.split('.').pop().toLowerCase();
      let parsed = [];

      if (extension === 'csv') {
        parsed = await parseCSVFile(file);
      } else if (extension === 'xlsx' || extension === 'xls') {
        parsed = await parseExcelFile(file);
      } else {
        throw new Error('Unsupported file type');
      }

      setParsedData(parsed);

      // Create validation context
      const context = createValidationContext(existingDishes, tags, cuisines);

      // Map rows to dish data
      const mapped = parsed.map(row => mapRowToDishData(row, context));
      setMappedDishes(mapped);

      // Validate rows
      const validation = await batchValidateRows(parsed, context, language);
      setValidationResults(validation);

      // Select all valid rows by default
      const validIndices = new Set();
      validation.forEach((result, index) => {
        if (result.isValid) {
          validIndices.add(index);
        }
      });
      setSelectedRows(validIndices);

      toastRef.current?.addToast(
        getTranslation('import.fileParsed', language) || `Parsed ${parsed.length} rows`,
        'success'
      );
    } catch (error) {
      toastRef.current?.addToast(
        error.message || getTranslation('import.importErrorMessages.parseFailed', language) || 'Failed to parse file',
        'error'
      );
      setDataFile(null);
      setParsedData([]);
    } finally {
      setParsing(false);
    }
  }, [existingDishes, tags, cuisines, language]);

  // Handle image ZIP file selection
  const handleImageZipSelect = useCallback((file, error) => {
    if (error) {
      toastRef.current?.addToast(error, 'error');
      return;
    }
    setImageZipFile(file);
  }, []);

  // Handle row selection
  const handleRowSelect = useCallback((row, index, checked) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((indices) => {
    setSelectedRows(new Set(indices));
  }, []);

  // Handle deselect all
  const handleDeselectAll = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  // Handle import
  const handleImport = useCallback(async () => {
    if (selectedRows.size === 0) {
      toastRef.current?.addToast(
        getTranslation('import.importErrorMessages.noRowsSelected', language) || 'Please select at least one row to import',
        'error'
      );
      return;
    }

    // Filter selected rows (only valid ones)
    const selectedIndices = Array.from(selectedRows);
    const validSelected = selectedIndices.filter(index => {
      const validation = validationResults[index];
      return validation && validation.isValid;
    });

    if (validSelected.length === 0) {
      toastRef.current?.addToast(
        getTranslation('import.importErrorMessages.noValidRows', language) || 'No valid rows selected. Please select valid rows only.',
        'error'
      );
      return;
    }

    setCurrentStep(3);
    setImporting(true);
    setImportProgress(0);

    // Create abort controller for cancel functionality
    abortControllerRef.current = new AbortController();

    try {
      // Create filtered file with only selected rows for better performance
      let fileToUpload = dataFile;
      
      // Only create filtered file if not all rows are selected
      // This reduces upload size and backend processing time
      if (selectedRows.size > 0 && selectedRows.size < parsedData.length) {
        try {
          fileToUpload = await createFilteredFile(dataFile, parsedData, selectedRows);
          // Filtered file created successfully - reduces upload size
        } catch (filterError) {
          // Fallback to original file if filtering fails
          // This is non-critical - backend can handle full file
          fileToUpload = dataFile;
        }
      }

      // Use real upload progress tracking with abort signal
      const result = await importDishesFromFile(
        fileToUpload, 
        imageZipFile,
        (progress) => {
          setImportProgress(progress);
        },
        abortControllerRef.current.signal
      );

      setImportProgress(100);

      // Handle response - backend returns DishImportResultDto
      const successCount = result.successCount ?? 0;
      const failedCount = result.failedCount ?? 0;
      const errors = result.errors || [];

      setImportResults({
        success: successCount,
        failed: failedCount,
        errors: errors
      });

      setCurrentStep(4);

      // Show appropriate message based on results
      if (successCount > 0 && failedCount === 0) {
        toastRef.current?.addToast(
          getTranslation('import.importSuccess', language) || `Successfully imported ${successCount} dish${successCount !== 1 ? 'es' : ''}`,
          'success'
        );
      } else if (successCount > 0 && failedCount > 0) {
        const plural = successCount !== 1 ? 'es' : '';
        let message = getTranslation('import.importPartialSuccess', language);
        if (message) {
          message = message.replace('{count}', successCount).replace('{plural}', plural).replace('{failed}', failedCount);
        } else {
          message = `Imported ${successCount} dish${plural}, ${failedCount} failed`;
        }
        toastRef.current?.addToast(message, 'warning');
      } else if (successCount === 0 && failedCount > 0) {
        const plural = failedCount !== 1 ? 'es' : '';
        let message = getTranslation('import.importAllFailed', language);
        if (message) {
          message = message.replace('{count}', failedCount).replace('{plural}', plural);
        } else {
          message = `All ${failedCount} dish${plural} failed to import`;
        }
        toastRef.current?.addToast(message, 'error');
      } else {
        toastRef.current?.addToast(
          getTranslation('import.importComplete', language) || 'Import complete',
          'info'
        );
      }

      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      // Don't show error toast if import was cancelled
      if (error.message === 'Import cancelled' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        toastRef.current?.addToast(
          getTranslation('import.importCancelled', language) || 'Import cancelled',
          'info'
        );
        setCurrentStep(2); // Go back to preview
      } else {
        // Extract error message from backend response
        let errorMessage = getTranslation('import.importErrorMessages.importFailed', language) || 'Import failed';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.developerMessage) {
          errorMessage = error.response.data.developerMessage;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toastRef.current?.addToast(
          errorMessage,
          'error'
        );
        setCurrentStep(2); // Go back to preview
      }
    } finally {
      setImporting(false);
      abortControllerRef.current = null;
    }
  }, [selectedRows, validationResults, dataFile, parsedData, imageZipFile, language, onImportComplete]);

  // Handle download template
  const handleDownloadTemplate = useCallback(async () => {
    try {
      const blob = await downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `menu_import_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toastRef.current?.addToast(
        getTranslation('import.templateDownloaded', language) || 'Template downloaded successfully',
        'success'
      );
    } catch (error) {
      toastRef.current?.addToast(
        error.message || getTranslation('import.importErrorMessages.templateDownloadFailed', language) || 'Failed to download template',
        'error'
      );
    }
  }, [language]);

  // Reset modal state
  const handleClose = useCallback(() => {
    // Cancel any ongoing import
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setCurrentStep(1);
    setDataFile(null);
    setImageZipFile(null);
    setParsedData([]);
    setMappedDishes([]);
    setValidationResults([]);
    setSelectedRows(new Set());
    setImportResults(null);
    setImportProgress(0);
    setImporting(false);
    onClose();
  }, [onClose]);

  // Step 1: File Selection
  const renderFileSelection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9] mb-2">
          {getTranslation('import.selectDataFile', language) || 'Select Data File'}
        </h3>
        <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-4">
          {getTranslation('import.dataFileDescription', language) || 'Upload a CSV or Excel file containing dish data'}
        </p>
        <FileUpload
          onFileSelect={handleDataFileSelect}
          acceptedTypes={['.csv', '.xlsx', '.xls']}
          maxSize={10}
          label={getTranslation('import.dragDropDataFile', language) || 'Drag and drop your data file here'}
          acceptedLabelTemplate={getTranslation('import.acceptedTypesLabel', language) || 'Accepted: {types} (max {size}MB)'}
          selectFileLabel={getTranslation('import.selectFile', language) || 'Select File'}
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9] mb-2">
          {getTranslation('import.selectImageZip', language) || 'Select Image ZIP (Optional)'}
        </h3>
        <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-4">
          {getTranslation('import.imageZipDescription', language) || 'Upload a ZIP file containing dish images. Images should match the filenames in your data file.'}
        </p>
        <FileUpload
          onFileSelect={handleImageZipSelect}
          acceptedTypes={['.zip']}
          maxSize={50}
          label={getTranslation('import.dragDropImageZip', language) || 'Drag and drop your image ZIP file here'}
          acceptedLabelTemplate={getTranslation('import.acceptedTypesLabelZip', language) || 'Accepted: {types} (max {size}MB)'}
          selectFileLabel={getTranslation('import.selectFile', language) || 'Select File'}
        />
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-[#30363d]">
        <Button
          variant="secondary"
          size="md"
          onClick={handleDownloadTemplate}
        >
          {getTranslation('import.downloadTemplate', language) || 'Download Template'}
        </Button>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="md"
            onClick={handleClose}
          >
            {getTranslation('common.cancel', language) || 'Cancel'}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              if (parsedData.length > 0) {
                setCurrentStep(2);
              } else {
                toastRef.current?.addToast(
                  getTranslation('import.importErrorMessages.noDataFile', language) || 'Please select a data file first',
                  'error'
                );
              }
            }}
            disabled={parsing || parsedData.length === 0}
          >
            {parsing ? (
              <>
                <LoadingSpinner size="small" />
                <span className="ml-2">{getTranslation('import.parsing', language) || 'Parsing...'}</span>
              </>
            ) : (
              getTranslation('import.next', language) || 'Next'
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // Step 2: Preview
  const renderPreview = () => (
    <div className="space-y-4">
      <ImportPreview
        dishes={mappedDishes}
        validationResults={validationResults}
        selectedRows={selectedRows}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
      />

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-[#30363d]">
        <Button
          variant="secondary"
          size="md"
          onClick={() => setCurrentStep(1)}
        >
          {getTranslation('common.back', language) || 'Back'}
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleImport}
          disabled={selectedRows.size === 0 || importing}
        >
          {getTranslation('import.confirmImport', language) || 'Confirm Import'}
        </Button>
      </div>
    </div>
  );

  // Handle cancel import
  const handleCancelImport = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setImporting(false);
    setImportProgress(0);
    setCurrentStep(2); // Go back to preview
  }, []);

  // Step 3: Importing
  const renderImporting = () => (
    <div className="space-y-4 text-center">
      <LoadingSpinner />
      <h3 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9]">
        {getTranslation('import.importingDishes', language) || 'Importing dishes...'}
      </h3>
      <ProgressBar progress={importProgress} variant="primary" showLabel={true} />
      <p className="text-sm text-gray-600 dark:text-[#8b949e]">
        {getTranslation('import.pleaseWait', language) || 'Please wait while we import your dishes'}
      </p>
      <Button
        variant="secondary"
        size="md"
        onClick={handleCancelImport}
        className="mt-4"
      >
        {getTranslation('import.cancel', language) || 'Cancel'}
      </Button>
    </div>
  );

  // Step 4: Results
  const renderResults = () => (
    <div className="space-y-4">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
          <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9] mb-2">
          {getTranslation('import.importComplete', language) || 'Import Complete'}
        </h3>
      </div>

      {importResults && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                {getTranslation('import.success', language) || 'Success'}
              </div>
              <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                {importResults.success}
              </div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
              <div className="text-sm text-red-600 dark:text-red-400 font-medium">
                {getTranslation('import.failed', language) || 'Failed'}
              </div>
              <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                {importResults.failed}
              </div>
            </div>
          </div>
          
          {/* Show errors if any */}
          {importResults.errors && importResults.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-[#c9d1d9] mb-2">
                {getTranslation('import.errors', language) || 'Errors'}
              </h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {importResults.errors.slice(0, 10).map((error, index) => (
                  <div key={index} className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                    {getTranslation('import.errorRow', language) || 'Row'} {error.row}: {error.message || error.field || getTranslation('import.unknown', language) || 'Unknown error'}
                  </div>
                ))}
                {importResults.errors.length > 10 && (
                  <div className="text-xs text-gray-500 dark:text-[#8b949e] p-2">
                    {(getTranslation('import.moreErrors', language) || `... and {count} more errors`).replace('{count}', importResults.errors.length - 10)}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-[#30363d]">
        <Button
          variant="primary"
          size="md"
          onClick={handleClose}
        >
          {getTranslation('common.close', language) || 'Close'}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toastRef} />
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={getTranslation('import.title', language) || 'Import Menu'}
        size="4xl"
      >
        {loadingContext ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {currentStep === 1 && renderFileSelection()}
            {currentStep === 2 && renderPreview()}
            {currentStep === 3 && renderImporting()}
            {currentStep === 4 && renderResults()}
          </>
        )}
      </Modal>
    </>
  );
};

export default MenuImportModal;
